import type { RequestError } from '@autobid/nuxt-pinia-store/store/useAppStore'
import type { AppRouter } from '@autobid/strapi-integration/server/trpc/routers/index'
import type { TRPCLink } from '@trpc/client'
import type { AnyRouter } from '@trpc/server'
import type { Composer as I18n } from 'vue-i18n'
import { CSRF_ERROR_MESSAGE } from '@autobid/nuxt-csurf/src/constant'
import { useAppStore } from '@autobid/nuxt-pinia-store/store/useAppStore'
import { HEADERS } from '@autobid/strapi-integration/constants/HEADERS'
import { getRequestId } from '@autobid/strapi-integration/utils/getRequestId'
import { initSSG } from '@autobid/strapi-integration/utils/isSSG'
import { addBreadcrumb } from '@sentry/nuxt'
import { observable, tap } from '@trpc/server/observable'
import { createTRPCNuxtClient, httpBatchLink } from 'trpc-nuxt/client'

function errorLink<TRouter extends AnyRouter = AnyRouter>(requestError: Ref<RequestError | null>): TRPCLink<TRouter> {
  return () => {
    return ({ op, next }) => {
      return observable((observer) => {
        return next(op)
          .pipe(
            tap({
              error(error) {
                if (!import.meta.client) {
                  return
                }

                const errorMessage = error.message.toLowerCase()
                const isAPIDown = errorMessage.includes('fetch failed')
                const unableToFetch = errorMessage.includes('failed to fetch')
                const loadFailed = errorMessage.includes('load failed')

                if (unableToFetch || isAPIDown || loadFailed) {
                  requestError.value = {
                    code: navigator.onLine ? 503 : 408,
                  }

                  return
                }

                if (!error.data || !error.data.handled) {
                  return
                }

                const tokenMismatch = error.data.httpStatus === 403 && error.message === CSRF_ERROR_MESSAGE
                const showDialog = (tokenMismatch || error.data.httpStatus === 401 || error.data.httpStatus === 419)

                if (showDialog) {
                  const setMessageFromI18n = tokenMismatch || error.data.httpStatus === 401

                  requestError.value = {
                    code: error.data.httpStatus,
                    message: setMessageFromI18n ? undefined : error.message,
                  }
                }

                if (error.data.httpStatus === 408) {
                  requestError.value = {
                    code: navigator.onLine ? 503 : 408,
                  }
                }
              },
            }),
          )
          .subscribe(observer)
      })
    }
  }
}

function sentryBreadcrumbLink<TRouter extends AnyRouter = AnyRouter>(): TRPCLink<TRouter> {
  return () => {
    return ({ op, next }) => {
      op.context.requestId = getRequestId()

      return observable((observer) => {
        return next(op)
          .pipe(
            tap({
              next() {
                addBreadcrumb({
                  type: 'http',
                  category: 'fetch',
                  message: op.path,
                  level: 'info',
                  data: {
                    input: op.input,
                    path: op.path,
                    type: op.type,
                    requestId: op.context.requestId,
                  },
                })
              },
              error(result) {
                addBreadcrumb({
                  type: 'http',
                  category: 'fetch',
                  message: op.path,
                  level: 'error',
                  data: {
                    error: {
                      message: result.message,
                      name: result.name,
                      cause: result.cause,
                    },
                    input: op.input,
                    path: op.path,
                    type: op.type,
                    requestId: op.context.requestId,
                  },
                })
              },
            }),
          )
          .subscribe(observer)
      })
    }
  }
}

export default defineNuxtPlugin((app) => {
  const {
    SENTRY: { DISABLED: SENTRY_DISABLED },
    isSSG,
  } = useRuntimeConfig().public

  const headers = useRequestHeaders(['x-forwarded-for', 'cookie', HEADERS.PdfApiKey, HEADERS.PdfCerberAuthorization])
  const { requestError } = storeToRefs(useAppStore())
  const i18n = app.$i18n as I18n

  initSSG(isSSG)

  const batchLink = httpBatchLink({
    url: '/api/trpc',
    headers(opts) {
      const locale = i18n.locale.value
      const additionalHeaders
        = 'headers' in opts.opList[0].context
          ? opts.opList[0].context.headers
          : {}
      const requestId = opts.opList[0].context?.requestId

      return {
        ...headers,
        ...(additionalHeaders ?? {}),
        'accept-language': locale,
        'request-id': requestId ? String(requestId) : undefined,
        [HEADERS.IsClientSide]: import.meta.client ? 'true' : 'false',
      }
    },
    fetchOptions: {
      onResponse({ response }) {
        if (import.meta.client && response.status === 503) {
          const RELOAD_KEY = '__last_503_reload'
          const lastReload = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0)
          const now = Date.now()

          if (now - lastReload > 30_000) {
            sessionStorage.setItem(RELOAD_KEY, String(now))
            window.location.reload()
          }
        }

        if (import.meta.client && response.ok && response.headers.get('content-type')?.toLowerCase().includes('text/html')) {
          showError({
            message: i18n.t('error-page.invalid-response'),
            statusCode: 400,
            statusMessage: i18n.t('error-page.intercepted-by-firewall-or-anti-virus'),
          })
        }

        if (!response.ok && response.headers.get('content-type')?.toLowerCase().includes('text/html')) {
          showError({
            statusCode: response.status,
            statusMessage: response.statusText,
          })
        }
      },
    },

  })

  const trpc = createTRPCNuxtClient<AppRouter>({
    links: [
      ...(Number(SENTRY_DISABLED) ? [] : [sentryBreadcrumbLink()]),
      errorLink(requestError),
      batchLink,
    ],
  })

  return {
    provide: {
      trpc,
    },
  }
})
